// ═══════════════════════════════════════════════════════════════
// CancelJobModal — kill a job without destroying it.
//
// The reason is the point. Six months from now "customer sold the
// car" and "went somewhere cheaper" lead to completely different
// conversations, and neither is recoverable from a deleted job.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { X, Ban, Hash } from 'lucide-react';
import { jobsCol } from './database.js';
import { cancelJob } from './lib/db-crud.js';
import { CANCEL_REASONS, formatPrice } from './lib/job-helpers.js';
import { formatDocumentNumber } from './lib/job-number.js';
import { splitByStatus } from './lib/recommendations.js';

export default function CancelJobModal({ job, customer, onClose, onCancelled }) {
  const [reason, setReason] = useState('customer-declined');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const docNumber = formatDocumentNumber(job);
  const { pending } = splitByStatus(job.recommendations);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await cancelJob(jobsCol(), job, { reason, note, by: 'mechanic' });
      onCancelled?.();
      onClose();
    } catch (err) {
      console.error('Cancel job failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 style={{ flex: 1, margin: 0 }}>Cancel this job?</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="cancel-summary">
          {/* Name and price share one line; the description gets its
              own. Without the wrapper the column flex put the price on
              a line of its own, reading as a stray "· $684.50". */}
          <div className="cancel-summary-line">
            <strong>{customer?.name || 'Customer'}</strong>
            {Number(job.totalPrice) > 0 && <span> · {formatPrice(job.totalPrice)}</span>}
          </div>
          <span className="muted small">{job.description}</span>
        </div>

        <p className="muted small cancel-explainer">
          <Hash size={12} className="cancel-explainer-icon" />
          {docNumber
            ? <>Keeps its number (<strong>{docNumber}</strong>), its inspection and its
               photos. It moves to History, and you can undo this later.</>
            : <>Keeps its inspection and its photos. It moves to History, and you can undo
               this later.</>}
        </p>

        <form className="form" onSubmit={handleSubmit}>
          <div className="form-row">
            <span>Why?</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              {CANCEL_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <span>Notes (optional)</span>
            <textarea
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything worth remembering next time they call…"
            />
          </div>

          {pending.length > 0 && (
            <p className="muted small cancel-recs-note">
              {pending.length} unanswered recommendation
              {pending.length === 1 ? '' : 's'} will be recorded as declined, so
              {pending.length === 1 ? ' it shows' : ' they show'} up next time this
              customer is in.
            </p>
          )}

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Keep the job
            </button>
            <button type="submit" className="btn-danger" disabled={submitting}>
              <Ban size={16} strokeWidth={2.5} />
              {submitting ? 'Cancelling…' : 'Cancel job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
